import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setIsSubmitting(false);
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u08gdca23r0tr1ao0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(
          Array.from(formData.entries()).map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const aboutJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '亲密发声训练指南',
      url: 'https://auxnuyb.cn',
      description: '专业、温暖、科学的亲密发声学习平台，帮助成年伴侣提升亲密互动质量。',
      email: 'contact@auxnuyb.cn',
      foundingDate: '2024',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@auxnuyb.cn',
        availableLanguage: ['Chinese'],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 - 亲密发声训练指南',
      description: '了解亲密发声训练指南的品牌故事、专业团队和联系方式。',
      url: 'https://auxnuyb.cn/about',
      isPartOf: {
        '@type': 'WebSite',
        name: '亲密发声训练指南',
        url: 'https://auxnuyb.cn',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://auxnuyb.cn',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '关于我们',
          item: 'https://auxnuyb.cn/about',
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解亲密发声训练指南的品牌故事、专业团队和联系方式。我们致力于帮助成年伴侣提升亲密互动质量，探索声音表达的魅力。"
        keywords="关于我们,亲密发声,品牌介绍,联系方式,成人情感教育"
        canonicalPath="/about"
        jsonLd={aboutJsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb />
            <div className="py-8 md:py-12">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                了解亲密发声训练指南的品牌故事、专业理念与核心团队
              </p>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className="ri-heart-pulse-line text-lg"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                品牌故事
              </h2>
            </div>

            <div className="prose-custom">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                <strong>亲密发声训练指南</strong>诞生于一个简单而深刻的信念：每个人的声音都值得被自由地表达，每一次亲密互动都值得被充分地享受。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                我们的创始团队由心理咨询师、性教育专家和声音训练师组成。在多年的专业实践中，我们发现许多成年伴侣在亲密关系中面临着一个共同的问题——<strong>不知道如何用声音自然地表达自己的感受</strong>。社会文化的约束、成长经历的影响、以及缺乏正确的引导，让许多人在最亲密的时刻选择了沉默。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                我们相信，亲密发声不仅仅是技巧，更是一种<strong>自我认知、情感释放和关系建设</strong>的方式。通过科学、温暖、专业的指导，我们帮助每一位学习者找到属于自己的声音，在亲密关系中实现更深层次的连接和满足。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                自成立以来，我们已经帮助数千位学习者开启了声音表达之旅，收获了无数温暖的回馈。这些回馈激励着我们不断完善内容，坚持用最专业的知识和最真诚的态度服务每一位学习者。
              </p>
            </div>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">
              我们的理念
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {[
                {
                  icon: 'ri-shield-star-line',
                  title: '专业科学',
                  desc: '所有内容基于心理学、生理学和性教育学的研究成果，确保信息准确、方法有效。',
                },
                {
                  icon: 'ri-heart-2-line',
                  title: '温暖包容',
                  desc: '尊重每一位学习者的独特性，创造无评判的学习环境，让每个人都能安心探索。',
                },
                {
                  icon: 'ri-lightbulb-flash-line',
                  title: '实践导向',
                  desc: '注重可操作性，提供具体的练习方法和步骤，帮助学习者将知识转化为实际能力。',
                },
                {
                  icon: 'ri-team-line',
                  title: '伴侣共进',
                  desc: '鼓励伴侣共同学习和练习，将亲密发声作为增进关系的桥梁，而非个人的任务。',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mb-3">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 font-heading mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">
              用户心声
            </h2>
            <div className="flex flex-col gap-4">
              {[
                {
                  quote: '以前在亲密关系中总是不敢出声，怕被觉得奇怪。通过这个平台的学习，我慢慢放下了心理包袱，现在和伴侣的关系比以往任何时候都要亲密。',
                  author: '小杨',
                  role: '学习者 · 28岁',
                },
                {
                  quote: '最开始是抱着试试看的心态来的，没想到内容这么专业和深入。不只是教技巧，更重要的是帮我理解了发声背后的心理和情感意义。',
                  author: '思雨',
                  role: '学习者 · 32岁',
                },
                {
                  quote: '我和伴侣一起学习的体验特别好。这些内容成为了我们坦诚沟通的契机，让我们对彼此的了解更深了。真心推荐给每一对想要提升关系质量的伴侣。',
                  author: '阿杰',
                  role: '学习者 · 35岁',
                },
              ].map((testimonial, index) => (
                <div
                  key={index}
                  className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4 italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-medium">
                      {testimonial.author.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">
                        {testimonial.author}
                      </p>
                      <p className="text-xs text-foreground-500 whitespace-nowrap">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">
              联系我们
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮件</h3>
                    <p className="text-sm text-foreground-600">contact@auxnuyb.cn</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">响应时间</h3>
                    <p className="text-sm text-foreground-600">我们会在 24 小时内回复您的咨询</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-shield-check-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">隐私保护</h3>
                    <p className="text-sm text-foreground-600">您的所有信息均受严格保护，不会与第三方共享</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-900 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="about-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-900 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="about-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-900 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    />
                  </div>

                  <div className="antispam-field">
                    <label htmlFor="phone_alt">备用电话</label>
                    <input
                      type="text"
                      id="phone_alt"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </>
                    ) : (
                      <>
                        发送留言
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <div className="flex items-center gap-2 p-3 rounded-md bg-accent-100 text-accent-800 text-sm">
                      <i className="ri-check-line"></i>
                      留言已成功发送，我们会尽快回复您！
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 text-red-700 text-sm">
                      <i className="ri-error-warning-line"></i>
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}